import PropTypes from "prop-types";

import useDeviceDetect from "@hooks/useDeviceDetect";
import { useCurrentCurrencySymbol } from "@hooks/usePrices";
import useTieredDiscounts from "@hooks/useTieredDiscounts";

import { cn } from "@helpers/general";
import { ComponentType } from "@constants/ComponentType";
import COLORS from "@constants/Colors";
import DISCOUNT_TYPES from "@constants/DiscountTypes";

function TieredOfferProgressBar({ componentType, saleColors, eligibleAmount }) {
  const { isMobile } = useDeviceDetect();
  const currencySymbol = useCurrentCurrencySymbol();
  const tieredDiscount = useTieredDiscounts();

  if (!tieredDiscount.discounts) {
    return null;
  }

  const backgroundColorReached = saleColors?.tierBgColorLuxeReached ?? COLORS.POPPY;
  const borderColor = saleColors?.tierBorderColorLuxe ?? COLORS.BLACK;
  const textColor = saleColors?.tierTextColorLuxe ?? COLORS.BLACK;
  const textColorReached = saleColors?.tierTextColorLuxeReached ?? COLORS.WHITE;

  const progress = tieredDiscount?.discounts?.map(([threshold, discount], idx) => {
    const isFilled = eligibleAmount >= threshold;

    let additionalText = "";
    let thresholdText = threshold;
    if (tieredDiscount.type === DISCOUNT_TYPES.QUANTITY_PERCENT) {
      additionalText = threshold === 1 ? "Item" : "Items";
      if (idx === 2) {
        thresholdText = `${threshold}+`;
      }
    }
    const mainContent = `${tieredDiscount.wording}${thresholdText} ${additionalText}`;

    return {
      threshold,
      isFilled,
      mainContent,
      content: `${discount}% Off${idx === 0 && currencySymbol === "$" ? " + Free Shipping" : ""}`,
    };
  });

  return (
    <div
      className={cn(componentType === ComponentType.PDP && "pb-[15px] desktop:pb-[25px]")}
      style={{ color: textColor }}
    >
      <div className="flex gap-[5px] desktop:gap-[10px] w-full">
        {progress?.map((item, index) => (
          <div key={`progress-tiered-gift-${item.threshold}`} className="flex-1 flex-wrap  min-w-0">
            <div
              className="flex flex-col items-center relative h-[7px] rounded-[8px] mb-[5px] duration-300 transition [transition-property:background-color,color,width] [transition-timing-function:ease]"
              style={{
                backgroundColor: item.isFilled ? backgroundColorReached : "",
                border: `1px solid ${borderColor}`,
              }}
              data-testid={`progress-bar-${index}`}
            >
              &nbsp;
            </div>
            <div
              className={cn(
                "leading-[0]",
                componentType === ComponentType.ANNOUNCEMENT_BAR && !isMobile
                  ? "text-center"
                  : "text-left"
              )}
              style={{
                color: item.isFilled ? textColor : textColorReached,
              }}
              data-testid={`progress-text-${index}`}
            >
              <div className="typo-micro whitespace-break-spaces">
                <span className="whitespace-nowrap">{item.mainContent}&nbsp;/&nbsp;</span>
                <span className="font-bold">{item.content}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

TieredOfferProgressBar.propTypes = {
  componentType: PropTypes.oneOf(Object.values(ComponentType)).isRequired,
  saleColors: PropTypes.shape({
    tierTextColorLuxe: PropTypes.string,
    tierTextColorLuxeReached: PropTypes.string,
    tierBgColorLuxe: PropTypes.string,
    tierBgColorLuxeReached: PropTypes.string,
    tierBorderColorLuxe: PropTypes.string,
  }),
  eligibleAmount: PropTypes.number.isRequired,
};

export default TieredOfferProgressBar;
